import React, { useCallback, useEffect, useMemo, useState } from "react";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Box, makeStyles, Typography, Tooltip, Popper } from "@material-ui/core";
import { PopperProps } from "@material-ui/core/Popper";
import SearchIcon from "@material-ui/icons/Search";
import { useDataProvider } from "react-admin";
import {
  getDriversByIds,
  getBusAssistantsByIds,
  getVehiclesByIds,
} from "../../enhancedRoutePlans/tools";

interface AssignmentSearchOption {
  assignmentId: number;
  routeName: string;
  plateNumber: string;
  driverMobile: string;
  driverName: string;
}

interface AssignmentSearchAutocompleteProps {
  startedAssignments: any[];
  selectedAssignmentId: number | null;
  onSelect: (assignmentId: number) => void;
  onClear: () => void;
  popperContainer?: React.RefObject<HTMLElement>;
}

const useStyles = makeStyles((theme) => ({
  root: {
    minWidth: 360,
    [theme.breakpoints.down("sm")]: {
      minWidth: 200,
    },
  },
  inputRoot: {
    backgroundColor: "#fff",
    borderRadius: "2px",
    fontSize: "15px",
    minHeight: "40px",
    boxShadow: "rgba(0, 0, 0, 0.3) 0px 2px 6px",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "&:hover": {
      backgroundColor: "#f9f9f9",
    },
  },
  option: {
    padding: "8px 16px",
    "&:hover": {
      backgroundColor: "#F4F7FE",
    },
  },
  optionRouteName: {
    fontWeight: 600,
    color: "#2B3674",
    fontSize: "14px",
  },
  optionDetail: {
    color: "#A3AED0",
    fontSize: "13px",
  },
  noOptions: {
    color: "#A3AED0",
    fontSize: "14px",
    padding: "8px 16px",
  },
  paper: {
    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
    borderRadius: "8px",
    marginTop: "4px",
  },
}));

const AssignmentSearchAutocomplete: React.FC<
  AssignmentSearchAutocompleteProps
> = ({ startedAssignments, selectedAssignmentId, onSelect, onClear, popperContainer }) => {
  const classes = useStyles();
  const provider = useDataProvider();

  const CustomPopper = useCallback(
    (props: PopperProps) => (
      <Popper
        {...props}
        container={popperContainer?.current}
        style={{ ...props.style, zIndex: 1300 }}
      />
    ),
    [popperContainer],
  );

  const [driverMap, setDriverMap] = useState<
    Record<number, { fullName: string; contactNumber: string }>
  >({});
  const [busAssistantMap, setBusAssistantMap] = useState<
    Record<number, { fullName: string; contactNumber: string }>
  >({});

  const [vehicleMap, setVehicleMap] = useState<
    Record<number, { plateNumber: string }>
  >({});

  // Fetch driver info for all assignments
  useEffect(() => {
    if (startedAssignments.length === 0) return;

    const fetchDrivers = async () => {
      try {
        // Collect all unique IDs to fetch
        const driverIds: number[] = [];
        const busAssistantIds: number[] = [];
        const vehicleIds: number[] = [];

        startedAssignments.forEach((assignment) => {
          if (assignment.driverId && !driverIds.includes(assignment.driverId)) {
            driverIds.push(assignment.driverId);
          }

          if (
            assignment.busAssistantId &&
            !busAssistantIds.includes(assignment.busAssistantId)
          ) {
            busAssistantIds.push(assignment.busAssistantId);
          }

          if (
            assignment.vehicleId &&
            !vehicleIds.includes(assignment.vehicleId)
          ) {
            vehicleIds.push(assignment.vehicleId);
          }
        });

        const promises = [];

        // Fetch drivers
        if (driverIds.length > 0) {
          promises.push(
            getDriversByIds(provider, driverIds).then((drivers) => {
              const map: Record<
                number,
                { fullName: string; contactNumber: string }
              > = {};
              drivers.forEach((driver: any) => {
                map[driver.id] = {
                  fullName: driver.fullName || "-",
                  contactNumber: driver.contactNumber || "-",
                };
              });
              setDriverMap(map);
            }),
          );
        }

        // Fetch bus assistants
        if (busAssistantIds.length > 0) {
          promises.push(
            getBusAssistantsByIds(provider, busAssistantIds).then(
              (busAssistants) => {
                const map: Record<
                  number,
                  { fullName: string; contactNumber: string }
                > = {};
                busAssistants.forEach((ba: any) => {
                  map[ba.id] = {
                    fullName: ba.fullName || "-",
                    contactNumber: ba.contactNumber || "-",
                  };
                });
                setBusAssistantMap(map);
              },
            ),
          );
        }

        // Fetch vehicles
        if (vehicleIds.length > 0) {
          promises.push(
            getVehiclesByIds(provider, vehicleIds).then((vehicles) => {
              const map: Record<number, { plateNumber: string }> = {};
              vehicles.forEach((vehicle: any) => {
                map[vehicle.id] = {
                  plateNumber: vehicle.plateNumber || "-",
                };
              });
              setVehicleMap(map);
            }),
          );
        }

        await Promise.all(promises);
      } catch (error) {
        console.error("Failed to fetch related data for search:", error);
      }
    };

    fetchDrivers();
  }, [startedAssignments]);

  // Build search options
  const options: AssignmentSearchOption[] = useMemo(() => {
    return startedAssignments.map((assignment) => {
      const driver = assignment.driverId
        ? driverMap[assignment.driverId]
        : null;
      const busAssistant = assignment.busAssistantId
        ? busAssistantMap[assignment.busAssistantId]
        : null;
      const vehicle = assignment.vehicleId
        ? vehicleMap[assignment.vehicleId]
        : null;

      const mobiles = [
        driver?.contactNumber,
        busAssistant?.contactNumber,
      ].filter((m) => m && m !== "-");
      const names = [driver?.fullName, busAssistant?.fullName].filter(
        (n) => n && n !== "-",
      );

      return {
        assignmentId: assignment.id,
        routeName: assignment.routeName || "-",
        plateNumber: vehicle?.plateNumber || "-",
        driverMobile: mobiles.length > 0 ? mobiles.join(" / ") : "-",
        driverName: names.length > 0 ? names.join(" / ") : "-",
      };
    });
  }, [startedAssignments, driverMap, busAssistantMap, vehicleMap]);

  // Derive the controlled value from selectedAssignmentId
  const selectedValue = useMemo(() => {
    if (selectedAssignmentId == null) return null;
    return (
      options.find((opt) => opt.assignmentId === selectedAssignmentId) || null
    );
  }, [selectedAssignmentId, options]);

  const filterOptions = (
    opts: AssignmentSearchOption[],
    { inputValue }: { inputValue: string },
  ) => {
    const search = inputValue.toLowerCase().trim();
    if (!search) return opts;

    return opts.filter(
      (opt) =>
        opt.routeName.toLowerCase().includes(search) ||
        opt.plateNumber.toLowerCase().includes(search) ||
        opt.driverMobile.toLowerCase().includes(search) ||
        opt.driverName.toLowerCase().includes(search),
    );
  };

  return (
    <Autocomplete<AssignmentSearchOption>
      id="assignment-search-autocomplete"
      PopperComponent={popperContainer ? CustomPopper : undefined}
      options={options}
      value={selectedValue}
      getOptionLabel={(option) =>
        `${option.routeName} | ${option.plateNumber} | ${option.driverMobile}`
      }
      getOptionSelected={(option, value) =>
        option.assignmentId === value.assignmentId
      }
      filterOptions={filterOptions}
      noOptionsText="No matching assignment found"
      onChange={(_event, value) => {
        const selected = value as AssignmentSearchOption | null;
        if (selected) {
          onSelect(selected.assignmentId);
        } else {
          onClear();
        }
      }}
      classes={{
        root: classes.root,
        inputRoot: classes.inputRoot,
        option: classes.option,
        noOptions: classes.noOptions,
        paper: classes.paper,
      }}
      renderOption={(option) => (
        <Box>
          <Typography className={classes.optionRouteName}>
            {option.routeName}
          </Typography>
          <Typography className={classes.optionDetail}>
            🚌 {option.plateNumber} &nbsp;|&nbsp; 📱 {option.driverMobile}
          </Typography>
        </Box>
      )}
      renderInput={(params) => (
        <Tooltip
          title="Search route, plate number, or contact number"
          placement="left-start"
          arrow
          PopperProps={{
            container: popperContainer?.current,
          }}
        >
          <TextField
            {...params}
            variant="outlined"
            size="small"
            placeholder="Search route, plate number, or contact number"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <SearchIcon style={{ color: "#A3AED0", marginRight: 4 }} />
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
          />
        </Tooltip>
      )}
    />
  );
};

export default AssignmentSearchAutocomplete;
